import { addDays, isAfter, isBefore, isDate, isValid, subDays } from 'date-fns';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@base-app';
import { COMMON_TRANSLATION } from '@common/constants';
import { Variants } from '@common/types';
import { clearTimeFromDate, toLocaleDateString } from '@common/utils';

import { InputDateProps } from './InputDate.types';
import { INPUT_DATE_PATTERN } from '@/common/components/constants';

const InputDate = forwardRef<HTMLInputElement, InputDateProps>((props, ref) => {
  const {
    deps = [],
    id,
    label,
    maxDate,
    minDate,
    name,
    onChange,
    required,
    variant = Variants.PRIMARY,
    value,
    ...rest
  } = props;
  const { t } = useTranslation(COMMON_TRANSLATION);

  const { control } = useFormContext();

  const valueFormatter = (value: Date | null | undefined): string => {
    if (!value) {
      return '';
    }

    if (isDate(value) && isValid(value)) {
      return toLocaleDateString(value);
    }

    return value.toString();
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        deps,
        required: required ? t('form.errors.required', { label }) : false,
        pattern: {
          value: INPUT_DATE_PATTERN,
          message: t('form.date.errors.invalid', {
            label,
          }),
        },
        validate: {
          minDate: (value: string) => {
            if (minDate && value && isDate(minDate)) {
              return (
                isBefore(clearTimeFromDate(subDays(minDate, 1)), clearTimeFromDate(new Date(value))) ||
                t('form.date.errors.min', {
                  date: toLocaleDateString(subDays(minDate, 1)),
                })
              );
            }
          },
          maxDate: (value: string) => {
            if (maxDate && value && isDate(maxDate)) {
              return (
                isAfter(clearTimeFromDate(addDays(maxDate, 1)), clearTimeFromDate(new Date(value))) ||
                t('form.date.errors.max', {
                  date: toLocaleDateString(addDays(maxDate, 1)),
                })
              );
            }
          },
        },
      }}
      render={({ field, fieldState }) => {
        return (
          <Input
            {...field}
            id={id}
            label={label}
            ref={ref ?? field.ref}
            onChange={(e) => {
              if (onChange) {
                onChange(e);
              }

              field.onChange(e);
            }}
            error={!(fieldState.error == null)}
            errorDescription={fieldState.error?.message as string}
            placeholder={t('form.date.placeholder')}
            testId={id}
            type="text"
            variant={variant}
            value={valueFormatter(value)}
            {...rest}
          />
        );
      }}
    />
  );
});

InputDate.displayName = 'InputDate';

export default InputDate;
